import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../../store';
import {
  isEquipmentVisibleForDxfLayers,
  isEquipmentVisibleForProcesses,
  equipmentReviewStatus,
} from '../../utils/equipmentGeometry';
import type { EquipmentInstance } from './equipmentTypes';

/**
 * Overlay instances allowed to leave the app — layout exports, backend saves and
 * the AMHS simulation payload all use this instead of the raw instance map.
 *
 * Matches what the 3D equipment layer can show: owning DXF layer visible and
 * process not filtered out. The master equipment-layer toggle no longer blanks
 * the simulation payload when overlays are still drawn on the canvas.
 */
export const selectExportableEquipmentInstances = createSelector(
  [
    (state: RootState) => state.equipment.instances,
    (state: RootState) => state.equipment.instanceOrder,
    (state: RootState) => state.equipment.hiddenProcesses,
    (state: RootState) => state.floorplan.layers,
    (state: RootState) => state.floorplan.hiddenEntityTypes,
  ],
  (instances, instanceOrder, hiddenProcesses, layers, hiddenEntityTypes) => {
    const result: EquipmentInstance[] = [];
    for (const id of instanceOrder) {
      const inst = instances[id];
      if (!inst) continue;
      if (equipmentReviewStatus(inst) !== 'accepted') continue;
      if (!isEquipmentVisibleForDxfLayers(inst, layers, hiddenEntityTypes)) continue;
      if (!isEquipmentVisibleForProcesses(inst, hiddenProcesses)) continue;
      result.push(inst);
    }
    return result;
  }
);

/** Same set as {@link selectExportableEquipmentInstances}, keyed by id. */
export const selectExportableEquipmentInstanceMap = createSelector(
  [selectExportableEquipmentInstances],
  (instances) => {
    const map: Record<string, EquipmentInstance> = {};
    for (const inst of instances) map[inst.id] = inst;
    return map;
  }
);
